import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalShell, StatusBadge } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import { productByIdFromSnapshot, useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";

export const Route = createFileRoute("/business/portal/orders/$orderId")({
  component: OrderDetailPage,
});

function OrderDetailPage() {
  const { orderId } = Route.useParams();
  const portal = useBusinessPortalSnapshot();
  const { account, orders, projects } = portal.data;
  const order = orders.find((item) => item.id === orderId);

  if (portal.loading) {
    return (
      <PortalShell active="/business/portal/orders" title="Loading order" description="Fetching order status." account={account}>
        <section className="b2b-card b2b-loading-card"><div /><div /><div /></section>
      </PortalShell>
    );
  }

  if (!order) {
    return (
      <PortalShell active="/business/portal/orders" title="Order not found" description="That order is not available in this workspace." account={account}>
        <Link to="/business/portal/orders" className="b2b-primary-button">Back to orders</Link>
      </PortalShell>
    );
  }

  const project = projects.find((item) => item.id === order.projectId);
  const product = productByIdFromSnapshot(portal.data, project?.productId ?? "");

  return (
    <PortalShell
      active="/business/portal/orders"
      eyebrow="Order"
      title={order.id}
      description="Track production, delivery, and reorder details."
      account={account}
      actions={<button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>Refresh</button>}
    >
      <SEOHead title={order.id} description="B2B order timeline and reorder details." path={`/business/portal/orders/${order.id}`} robots="noindex,follow" />

      <section className="b2b-two-column">
        <article className="b2b-card">
          <div className="b2b-card-row">
            <span className="b2b-kicker">Order summary</span>
            <StatusBadge status={order.status} tone={order.status === "delivered" ? "good" : "warn"} />
          </div>
          <h2>{project?.name ?? "Packaging order"}</h2>
          <dl>
            <div><dt>Packaging</dt><dd>{product?.title ?? "Packaging"}</dd></div>
            <div><dt>Ship target</dt><dd>{order.expectedShipDate || "Pending"}</dd></div>
            <div><dt>Status</dt><dd>{order.trackingLabel || "In progress"}</dd></div>
          </dl>
          <div className="b2b-action-row">
            <Link to="/business/portal/projects/new" className="b2b-primary-button">Reorder</Link>
            <Link to="/business/portal/orders" className="b2b-secondary-button">All orders</Link>
          </div>
        </article>

        <article className="b2b-card">
          <span className="b2b-kicker">Files</span>
          <h2>Order files</h2>
          {order.packageFiles.length > 0 ? (
            <ul className="b2b-file-list">
              {order.packageFiles.map((file) => <li key={file}>{file}</li>)}
            </ul>
          ) : (
            <p>Files will appear here after proof approval.</p>
          )}
        </article>
      </section>

      <section className="b2b-card">
        <span className="b2b-kicker">Timeline</span>
        <h2>Production progress</h2>
        {order.timeline.length > 0 ? (
          <ol className="b2b-timeline">
            {order.timeline.map((event) => (
              <li key={`${event.label}-${event.at}`} className={event.complete ? "is-complete" : ""}>
                <span>{event.at}</span>
                <div>
                  <strong>{event.label}</strong>
                  <p>{event.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p>Order timeline updates will appear here.</p>
        )}
      </section>
    </PortalShell>
  );
}
